import { css } from '@emotion/react';
import { times } from 'lodash';
import { useEffect } from 'react';
import { UIColorPalette } from './ColorPalette';

import { CardAffinity } from '@mythos/game/CardTypes';
import { SCAVENGE_CARD_DEF_IDS } from '@mythos/game/GameConstants';
import * as Rules from '@mythos/game/Rules';
import invariant from 'invariant';
import { MEDIA_QUERY_MOBILE } from 'mobile_ui/MStyles';
import { match } from 'ts-pattern';
import { useShallow } from 'zustand/shallow';
import { CardBack, CardBackType, CardBackView } from './CardBackView';
import CardView from './CardView';
import HoverCardStore from './HoverCardStore';
import PileView from './PileView';
import TributeCardView from './TributeCardView';
import { useGameDefinitionsStore } from './stores/gameDefinitionsStore';
import { useSessionStore } from './stores/sessionStore';

function AllCardsPage() {
  const session = useSessionStore((state) => state.session);
  const { gameDefinitions, fetchGameDefinitions } = useGameDefinitionsStore(
    useShallow((state) => ({
      gameDefinitions: state.gameDefinitions,
      fetchGameDefinitions: state.fetchGameDefinitions,
    })),
  );

  useEffect(() => {
    fetchGameDefinitions();
  }, [fetchGameDefinitions]);

  useEffect(() => {
    if (gameDefinitions) {
      // scroll to hash
      const hash = window.location.hash;
      if (hash) {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView();
        }
      }
    }
  }, [gameDefinitions]);

  const cardsByAge: {
    [k: number]: (Rules.CardWithID | CardBack)[];
    basic: (Rules.CardWithID | CardBack)[];
    leader: (Rules.CardWithID | CardBack)[];
  } = { basic: [], leader: [] };

  const cardDefs = gameDefinitions?.cardDefs || [];
  const tributeCardDefs = gameDefinitions?.tributeCardDefs || [];
  const cards = cardDefs.map((x, i) => ({
    ...x,
    index: i,
    id: `card${i}`,
  }));
  const tributeCards = tributeCardDefs.map((x, i) => ({
    ...x,
    index: i,
    id: `tribute${i}`,
  }));

  cardsByAge.leader = cards.filter((c) => c.affinity === CardAffinity.Leader);
  const basicCardsToRender = cards.filter((c) => {
    return (
      c.affinity === CardAffinity.Basic &&
      // render all scavenges but only age 1 settlements/mercs
      (c.age === 1 || SCAVENGE_CARD_DEF_IDS.has(c.defID))
    );
  });
  cardsByAge.basic = basicCardsToRender;
  times(3, function (i) {
    cardsByAge[i] = cards.filter((c) => {
      return c.age === i + 1 && c.affinity !== CardAffinity.Basic;
    });
    cardsByAge[i].unshift({
      id: `card-back-age${i + 1}`,
      type: `age${i + 1}` as CardBackType,
    });
  });

  type AllCardsSection = {
    title: string;
    linkHash: string;
    cards: { id: string }[];
    cardBack?: { id: string; type: CardBackType };
  };
  const sections: AllCardsSection[] = [
    {
      title: 'Leaders',
      linkHash: 'leader',
      cards: cardsByAge.leader,
    },
    {
      title: 'Basic Cards',
      linkHash: 'basic',
      cards: cardsByAge.basic,
    },
    {
      title: 'Age 1',
      linkHash: 'age-1',
      cards: cardsByAge[0],
    },
    {
      title: 'Age 2',
      linkHash: 'age-2',
      cards: cardsByAge[1],
    },
    {
      title: 'Age 3',
      linkHash: 'age-3',
      cards: cardsByAge[2],
    },
    {
      title: 'Tributes',
      linkHash: 'tributes',
      cards: tributeCards,
    },
  ];

  gameDefinitions?.modules.forEach((module) => {
    if (
      module.state === 'enabled' ||
      (module.state === 'devOnly' && session?.getUser().isDev)
    ) {
      const cards = module.cardDefs.map((x, i) => ({
        ...x,
        index: i,
        id: `card${i}`,
      }));
      const tributeCards = module.tributeCardDefs.map((x, i) => ({
        ...x,
        index: i,
        id: `tribute${i}`,
      }));

      sections.push({
        title: module.name,
        linkHash: module.key,
        cards: [...cards, ...tributeCards],
      });
    }
  });

  const renderer = (props: any) => {
    var card = props.card;
    return match(card.id)
      .when(
        (id) => id.startsWith('card-back'),
        () => <CardBackView {...props} />,
      )
      .when(
        (id) => id.startsWith('tribute'),
        () => <TributeCardView {...props} />,
      )
      .when(
        (id) => id.startsWith('card'),
        () => <CardView {...props} />,
      )
      .otherwise((x) => invariant(false, 'unknown card id ' + x));
  };
  const hover_renderer = renderer;

  return (
    <div css={AllCardsStyles.container}>
      <div css={AllCardsStyles.linksContainer}>
        {sections.map(({ title, linkHash }) => {
          return (
            <a css={AllCardsStyles.link} href={`#${linkHash}`} key={linkHash}>
              {title}
            </a>
          );
        })}
      </div>
      <div>
        {sections.map(({ title, cards, linkHash }, i) => {
          return (
            <div css={AllCardsStyles.pileContainer} key={i} id={linkHash}>
              <h1>{title}</h1>
              <PileView
                css={AllCardsStyles.pile}
                renderer={renderer}
                hoverRenderer={hover_renderer}
                cards={cards}
              />
            </div>
          );
        })}
        {HoverCardStore.getHoverCardComponent()}
      </div>
    </div>
  );
}

const AllCardsStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  }),
  linksContainer: css({
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    gap: 24,

    alignSelf: 'center',

    backgroundColor: UIColorPalette.lobbyDarkerBG,
    padding: 10,
    paddingLeft: 30,
    paddingRight: 30,
    marginLeft: 20,
    marginRight: 20,

    [MEDIA_QUERY_MOBILE]: {
      position: 'sticky',
      zIndex: 100,
      top: 0,
      width: '100%',

      padding: 8,
      margin: 0,
      gap: 4,
      flexWrap: 'wrap',
    },
  }),
  link: css({
    textDecoration: 'none',

    color: UIColorPalette.pageHeaderLink,
    textAlign: 'center',
    '&:hover': {
      color: UIColorPalette.pageHeaderLinkHover,
    },

    flexGrow: 1,
  }),
  pileContainer: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'center',

    backgroundColor: UIColorPalette.allCardsPileBG,
    color: UIColorPalette.allCardsPileText,

    textAlign: 'center',

    padding: 5,
    paddingRight: 10,
    paddingLeft: 10,
    margin: 5,

    [MEDIA_QUERY_MOBILE]: {
      alignItems: 'center',
    },
  }),
  pile: css({
    justifyContent: 'flex-start',

    [MEDIA_QUERY_MOBILE]: {
      justifyContent: 'center',
    },
  }),
};

export default AllCardsPage;
